const currencyFmt = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const monthFmt = new Intl.DateTimeFormat('fr-FR', {
  month: 'long',
  year: 'numeric',
});

/**
 * Formate un montant en centimes vers une chaîne Euro.
 * Ex : 1762134 → "17 621,34 €"
 */
export function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '—';
  return currencyFmt.format(cents / 100);
}

/**
 * Formate une date ISO (string ou Date) en format FR : "19/04/2026"
 */
export function formatDate(iso) {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return '—';
  return dateFmt.format(d);
}

/**
 * Formate un couple {year, month} (1-indexed) en "avril 2026"
 */
export function formatMonth(year, month) {
  const d = new Date(year, month - 1, 1);
  return monthFmt.format(d);
}

/**
 * Abrège un montant en euros (pour les axes de graphiques).
 * Ex : 1250000 → "12,5 k€" (depuis centimes)
 * Ex : 120000000 → "1,2 M€" (depuis centimes)
 */
export function formatCurrencyShort(cents) {
  if (cents === null || cents === undefined) return '—';
  const euros = cents / 100;
  if (Math.abs(euros) >= 1_000_000) return `${(euros / 1_000_000).toFixed(1)} M€`;
  if (Math.abs(euros) >= 10_000) return `${(euros / 1_000).toFixed(1)} k€`;
  if (Math.abs(euros) >= 1_000) return `${(euros / 1_000).toFixed(1)} k€`;
  return `${euros.toFixed(0)} €`;
}

/**
 * Convertit un code ISO-2 pays en emoji drapeau.
 * Ex : 'FR' → '🇫🇷', 'DE' → '🇩🇪'
 * Retourne '🌍' si le code est absent, inconnu ou invalide.
 */
export function flagEmoji(countryCode) {
  if (!countryCode || countryCode === 'unknown' || countryCode.length !== 2) return '🌍';
  const code = countryCode.toUpperCase();
  // Chaque lettre A=0x1F1E6, Z=0x1F200 — offset depuis 'A' (65)
  return String.fromCodePoint(
    0x1f1e6 + code.charCodeAt(0) - 65,
    0x1f1e6 + code.charCodeAt(1) - 65
  );
}

/**
 * Formate un montant avec séparation HT/TTC quand la TVA est connue.
 *
 * Convention signe : vatAmountCents est signé naturellement (négatif sur dépense,
 * positif sur recette). HT = TTC − TVA (même signe que TTC).
 *
 * - Si vatAmountCents est null, 0 ou undefined → retourne juste "X €" (TTC seul).
 * - Sinon → retourne "X € HT (Y € TTC)".
 *
 * @param {number} amountCents      Montant TTC en centimes (signé).
 * @param {number|null} vatAmountCents  TVA en centimes (signée), ou null.
 * @returns {string}
 */
export function formatHtTtc(amountCents, vatAmountCents) {
  if (amountCents === null || amountCents === undefined) return '—';
  if (!vatAmountCents) return formatCurrency(amountCents);
  const htCents = amountCents - vatAmountCents;
  return `${formatCurrency(htCents)} HT (${formatCurrency(amountCents)} TTC)`;
}
